import { notifyAfterTransition } from '../handlers/notification.rules'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from './context'

/**
 * Emulasi Workflow Studio engine (`SubmissionServiceV2.setSubmission`) di atas tabel `wml_*`/`wxl_*`
 * yang tersimpan di local DB. Urutan langkah mengikuti
 * `packages/lib/src/service/workflow-action/submission-v2/docs/submission-v2-system-behavior.md`.
 *
 * Batas emulasi (sama dengan engine produksi saat ini): approval quorum > 1 tidak diakumulasi,
 * e-mail dan SLA tidak dijalankan, cross-workflow tidak dijalankan.
 */

export class WorkflowError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    public readonly status = 400
  ) {
    super(message)
  }
}

export type SubmitWorkflowPayload = {
  workflow_id: string
  action_id: string
  submission_id?: string | null
  entity_id?: string | null
  actor_id?: string | null
  reason_text?: string
  native_document_payload?: { path?: string; payload?: Record<string, unknown> }
}

const idOf = (value: unknown) =>
  typeof value === 'object' && value !== null ? String((value as { id?: unknown }).id ?? '') : String(value ?? '')

const isEmptyId = (value: unknown) =>
  value === undefined || value === null || value === '' || value === 'null' || value === 'undefined'

/**
 * `wml_actor.condition` ditulis gaya Directus (`{ created_by: { _eq: '$CURRENT_USER.id' } }`,
 * TL 12B §3). CASL di FE memakai MongoQuery, jadi variabel user di-resolve dan operator
 * diterjemahkan (`_eq` → `$eq`, dst.) sebelum dikirim. [GAP-BE-WF-01]: endpoint produksi
 * saat ini mengirim `meta_config` mentah tanpa langkah ini.
 */
const CASL_OPERATOR: Record<string, string> = {
  _eq: '$eq',
  _neq: '$ne',
  _in: '$in',
  _nin: '$nin',
  _gt: '$gt',
  _gte: '$gte',
  _lt: '$lt',
  _lte: '$lte'
}

function resolveUserVariable(value: unknown): unknown {
  if (typeof value === 'string' && value.startsWith('$CURRENT_USER')) {
    const field = value.split('.')[1] ?? 'id'
    const user = findRow('directus_users', currentUserId())
    return user?.[field] ?? null
  }
  return Array.isArray(value) ? value.map(resolveUserVariable) : value
}

export function toCaslCondition(condition: unknown): Record<string, unknown> {
  if (!condition || typeof condition !== 'object') return {}
  return Object.fromEntries(
    Object.entries(condition as Record<string, Record<string, unknown>>).map(([field, rule]) => [
      field,
      rule && typeof rule === 'object'
        ? Object.fromEntries(
            Object.entries(rule).map(([operator, value]) => [
              CASL_OPERATOR[operator] ?? operator,
              resolveUserVariable(value)
            ])
          )
        : rule
    ])
  )
}

/** Evaluasi server-side kondisi actor terhadap row entity (`created_by` ⇢ kolom audit `user_created`). */
/**
 * Penjaga aksi per `{kode workflow}:{nama aksi}` — aturan bisnis yang tidak bisa dinyatakan lewat eligibility actor,
 * mis. Kadiv baru boleh memutuskan setelah ambang disposisi Master SLA (dan hanya bila disposisinya menyala). Penjaga
 * melempar `WorkflowError` untuk menolak; dijalankan setelah pemeriksaan status & actor, sebelum transisi ditulis.
 */
type ActionGuard = (entityRow: LocalRow | undefined) => void
const ACTION_GUARDS = new Map<string, ActionGuard>()
export function registerActionGuard(workflowCode: string, actionNames: string[], guard: ActionGuard) {
  for (const name of actionNames) ACTION_GUARDS.set(`${workflowCode}:${name}`, guard)
}

function actorConditionHolds(condition: unknown, entityRow: LocalRow | undefined): boolean {
  const casl = toCaslCondition(condition)
  return Object.entries(casl).every(([field, rule]) => {
    const raw = entityRow?.[field] ?? (field === 'created_by' ? entityRow?.user_created : undefined)
    const actual = idOf(raw)
    if (!rule || typeof rule !== 'object') return actual === String(rule)
    return Object.entries(rule as Record<string, unknown>).every(([operator, expected]) => {
      if (operator === '$eq') return actual === String(expected)
      if (operator === '$ne') return actual !== String(expected)
      if (operator === '$in') return (expected as unknown[]).map(String).includes(actual)
      if (operator === '$nin') return !(expected as unknown[]).map(String).includes(actual)
      return true
    })
  })
}

export function findWorkflowByCode(code: string): LocalRow | undefined {
  return readTable('wml_workflow').find(row => row.code === code)
}

export function startStatus(workflowId: string): LocalRow | undefined {
  const statuses = readTable('wml_status').filter(row => idOf(row.workflow_id) === workflowId)
  return statuses.find(row => row.is_start === true) ?? statuses.find(row => Number(row.order) === 1)
}

/** Actor milik workflow yang cocok dengan role persona aktif. */
export function actorsForRole(workflowId: string, roleId: string): LocalRow[] {
  return readTable('wml_actor').filter(
    row =>
      idOf(row.workflow_id) === workflowId &&
      (idOf(row.role_id) === roleId || idOf(row.direct_user_id) === currentUserId())
  )
}

/**
 * Bentuk response `GET /api/workflow/component_permission/:workflowCode[/:submissionId]`
 * (lihat `ComponentPermissionData` pada `atoms/workflow-studio-container`).
 */
export function componentPermissions(workflowCode: string, submissionId: string | undefined, roleId: string) {
  const workflow = findWorkflowByCode(workflowCode)
  if (!workflow) return []

  const submission = submissionId ? findRow('wxl_submission', submissionId) : undefined
  const status = submission ? findRow('wml_status', idOf(submission.current_status_id)) : startStatus(workflow.id)
  if (!status) return []

  const actorIds = new Set(actorsForRole(workflow.id, roleId).map(actor => actor.id))
  const components = readTable('wml_component').filter(row => idOf(row.workflow_id) === workflow.id)
  const componentById = new Map(components.map(component => [component.id, component]))
  const base = {
    status_id: status.id,
    status_name: status.name,
    status_code: status.code,
    workflow_id: workflow.id
  }

  const actionEntries = readTable('wml_action')
    .filter(action => idOf(action.workflow_id) === workflow.id && idOf(action.from_status_id) === status.id)
    .flatMap(action => {
      const component = componentById.get(idOf(action.component_id))
      if (!component) return []
      return readTable('wml_action_actor_eligibility')
        .filter(link => idOf(link.action_id) === action.id && actorIds.has(idOf(link.actor_id)))
        .map(link => {
          const actor = findRow('wml_actor', idOf(link.actor_id))
          return {
            ...base,
            component_code: component.code,
            component_name: component.name,
            component_type: 'ACTION',
            action_id: action.id,
            panel_id: null,
            panel_permission: null,
            color: (action.ui_config as { color?: string } | undefined)?.color ?? status.color ?? 'primary',
            actor_id: idOf(link.actor_id),
            condition: toCaslCondition(actor?.condition),
            ui_config: action.ui_config ?? component.ui_config ?? {}
          }
        })
    })

  const panelEntries = readTable('wml_panel')
    .filter(panel => idOf(panel.status_id) === status.id && componentById.has(idOf(panel.component_id)))
    .flatMap(panel => {
      const component = componentById.get(idOf(panel.component_id))!
      return readTable('wml_panel_actor_eligibility')
        .filter(link => idOf(link.panel_id) === panel.id && actorIds.has(idOf(link.actor_id)))
        .map(link => ({
          ...base,
          component_code: component.code,
          component_name: component.name,
          component_type: 'PANEL',
          action_id: null,
          panel_id: panel.id,
          panel_permission: panel.permission ?? 'EDIT',
          color: status.color ?? 'primary',
          actor_id: idOf(link.actor_id),
          condition: toCaslCondition(findRow('wml_actor', idOf(link.actor_id))?.condition),
          ui_config: component.ui_config ?? {}
        }))
    })

  return [...actionEntries, ...panelEntries]
}

/** Bentuk response `GET /api/workflow/action-config/:workflowCode/:componentCode`. */
export function actionConfig(workflowCode: string, componentCode: string, roleId: string) {
  const workflow = findWorkflowByCode(workflowCode)
  if (!workflow) throw new WorkflowError(`Workflow ${workflowCode} tidak ditemukan.`, 'WORKFLOW_NOT_FOUND', 404)
  const component = readTable('wml_component').find(
    row => idOf(row.workflow_id) === workflow.id && row.code === componentCode
  )
  if (!component) throw new WorkflowError(`Komponen ${componentCode} tidak ditemukan.`, 'COMPONENT_NOT_FOUND', 404)
  const actorIds = new Set(actorsForRole(workflow.id, roleId).map(actor => actor.id))
  /* Satu komponen bisa melayani beberapa aksi (mis. CMP_*_REJECT per tingkat approval). Pilih aksi yang boleh
   * dijalankan peran ini; bila tidak ada yang cocok, pakai aksi pertama agar error otorisasi tetap muncul. */
  const componentActions = readTable('wml_action').filter(row => idOf(row.component_id) === component.id)
  const eligibilityOf = (actionId: string) =>
    readTable('wml_action_actor_eligibility').find(
      link => idOf(link.action_id) === actionId && actorIds.has(idOf(link.actor_id))
    )
  const action = componentActions.find(row => eligibilityOf(String(row.id))) ?? componentActions[0]
  if (!action) throw new WorkflowError(`Action untuk ${componentCode} tidak ditemukan.`, 'ACTION_NOT_FOUND', 404)
  const eligibility = eligibilityOf(String(action.id))
  return {
    workflow_id: workflow.id,
    submission_id: null,
    action_id: {
      id: action.id,
      actor_id: eligibility ? idOf(eligibility.actor_id) : null,
      is_need_reason: Boolean(action.is_need_reason),
      is_reason_required: Boolean(action.is_reason_required)
    }
  }
}

/** Emulasi `POST /api/workflow/submission/v2` → `SetSubmissionResult`. */
export function setSubmission(payload: SubmitWorkflowPayload) {
  const workflow = findRow('wml_workflow', payload.workflow_id)
  if (!workflow) throw new WorkflowError('Workflow tidak ditemukan.', 'WORKFLOW_NOT_FOUND', 404)
  const action = findRow('wml_action', payload.action_id)
  if (!action) throw new WorkflowError('Action tidak ditemukan.', 'ACTION_NOT_FOUND', 404)
  if (idOf(action.workflow_id) !== workflow.id)
    throw new WorkflowError('Action bukan milik workflow ini.', 'ACTION_WORKFLOW_MISMATCH')

  const entity = String(workflow.entity)
  const nativePath = payload.native_document_payload?.path
  if (nativePath && nativePath !== entity)
    throw new WorkflowError(`native_document_payload.path harus "${entity}".`, 'ENTITY_PATH_MISMATCH')

  const reason = String(payload.reason_text ?? '').trim()
  if (action.is_need_reason && action.is_reason_required && !reason)
    throw new WorkflowError('Alasan wajib diisi untuk aksi ini.', 'REASON_REQUIRED')

  const nativePayload = { ...(payload.native_document_payload?.payload ?? {}) }
  let submission = isEmptyId(payload.submission_id) ? undefined : findRow('wxl_submission', payload.submission_id)
  if (!isEmptyId(payload.submission_id) && !submission)
    throw new WorkflowError('Submission tidak ditemukan.', 'SUBMISSION_NOT_FOUND', 404)
  if (submission && idOf(submission.workflow_id) !== workflow.id)
    throw new WorkflowError('Submission bukan milik workflow ini.', 'SUBMISSION_WORKFLOW_MISMATCH')

  const actor = payload.actor_id ? findRow('wml_actor', payload.actor_id) : undefined
  const userRoleIds = readTable('mt_privileges')
    .filter(row => idOf(row.user) === currentUserId())
    .map(row => idOf(row.role))
  const actorBelongsToUser =
    actor && (userRoleIds.includes(idOf(actor.role_id)) || idOf(actor.direct_user_id) === currentUserId())
  if (!actor || idOf(actor.workflow_id) !== workflow.id || !actorBelongsToUser)
    throw new WorkflowError('Actor tidak berhak menjalankan aksi ini.', 'ERR_UNAUTHORIZED_ROLE', 403)
  const eligible = readTable('wml_action_actor_eligibility').some(
    link => idOf(link.action_id) === action.id && idOf(link.actor_id) === actor.id
  )
  if (!eligible) throw new WorkflowError('Actor tidak berhak menjalankan aksi ini.', 'ERR_UNAUTHORIZED_ROLE', 403)

  let entityId = submission ? idOf(submission.entity_id) : ''
  if (!submission) {
    entityId = isEmptyId(payload.entity_id) ? String(nativePayload.id ?? '') : String(payload.entity_id)
    if (!entityId || !findRow(entity, entityId)) {
      const created = insertRow(entity, {
        ...auditFieldsForCreate(),
        ...nativePayload,
        id: entityId || newId()
      })
      entityId = created.id
    }
    const start = startStatus(workflow.id)
    submission = insertRow('wxl_submission', {
      ...auditFieldsForCreate(),
      id: newId(),
      workflow_id: workflow.id,
      entity,
      entity_id: entityId,
      source: 'ACTION',
      current_status_id: start?.id ?? null,
      last_action_id: null,
      workflow_snapshot: null
    })
  }

  const fromStatusId = idOf(submission.current_status_id)
  if (idOf(action.from_status_id) !== fromStatusId) {
    const current = findRow('wml_status', fromStatusId)
    throw new WorkflowError(
      `Aksi tidak berlaku untuk status saat ini (${current?.name ?? fromStatusId}).`,
      'INVALID_FROM_STATUS',
      409
    )
  }

  if (!actorConditionHolds(actor.condition, findRow(entity, entityId)))
    throw new WorkflowError(
      'Pembuat dokumen tidak boleh menyetujui dokumennya sendiri.',
      'ERR_SELF_APPROVAL_VIOLATION',
      409
    )

  ACTION_GUARDS.get(`${String(workflow.code)}:${String(action.name)}`)?.(findRow(entity, entityId))

  const toStatusId = idOf(action.to_status_id)
  const log = insertRow('wxl_submission_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    workflow_id: workflow.id,
    submission_id: submission.id,
    action_id: action.id,
    actor_id: payload.actor_id ?? null,
    from_status_id: fromStatusId,
    to_status_id: toStatusId,
    approval_total_count: 1,
    approval_approved_count: 1,
    approval_rejected_count: 0,
    reason: reason || null,
    entity,
    entity_id: entityId
  })

  updateRow('wxl_submission', submission.id, {
    current_status_id: toStatusId,
    last_action_id: action.id,
    ...auditFieldsForUpdate()
  })

  const { id: _ignoredId, ...nativePatch } = nativePayload
  const patchMapping = (action.entity_patch_mapping ?? {}) as Record<string, unknown>
  const resolvedMapping = Object.fromEntries(
    Object.entries(patchMapping).map(([key, value]) => [
      key,
      value === '$NOW' ? new Date().toISOString() : value === '$CURRENT_USER' ? currentUserId() || null : value
    ])
  )
  updateRow(entity, entityId, {
    ...nativePatch,
    ...resolvedMapping,
    status_id: toStatusId,
    submission_id: submission.id,
    ...auditFieldsForUpdate()
  })

  /** Pusat Notifikasi (GEN-03): aturan per workflow:aksi; tidak pernah melempar, jadi tidak menggagalkan transisi. */
  notifyAfterTransition({
    workflowCode: String(workflow.code),
    actionName: String(action.name),
    entity,
    entityId,
    reason,
    row: findRow(entity, entityId) as LocalRow
  })

  return {
    submission_id: submission.id,
    status_from: fromStatusId,
    status_to: toStatusId,
    action_id: action.id,
    approval: { required: 1, total_count: 1, approved_count: 1, rejected_count: 0, quorum_met: true },
    submission_log: { id: log.id, created_at: log.date_created },
    entity,
    entity_id: entityId
  }
}

/**
 * Aksi SISTEM (tanpa aktor manusia): pengganti worker SLA di local mode — auto-approve, auto-escalate, auto-complete.
 * Satu-satunya jalur untuk transisi otomatis: menulis log + submission + status entitas seperti `setSubmission`, lalu
 * menjalankan aturan notifikasi yang sama dengan aksi manual (`notifyAfterTransition`, mode `system`: user pembuka
 * halaman tidak dikecualikan, `dedupeKey` selalu terisi). Idempoten bila pemanggil menyaring status asal (sweep).
 */
export function runSystemAction(workflowCode: string, row: LocalRow, actionName: string, reason: string): void {
  const wf = readTable('wml_workflow').find(item => item.code === workflowCode) as LocalRow
  const action = readTable('wml_action').find(
    item => item.name === actionName && idOf(item.workflow_id) === wf.id
  ) as LocalRow
  const toStatus = idOf(action.to_status_id)
  const entity = String(wf.entity)
  insertRow('wxl_submission_log', {
    ...auditFieldsForCreate(),
    id: newId(),
    workflow_id: wf.id,
    submission_id: row.submission_id,
    action_id: action.id,
    actor_id: null,
    from_status_id: idOf(action.from_status_id),
    to_status_id: toStatus,
    approval_total_count: 1,
    approval_approved_count: 1,
    approval_rejected_count: 0,
    reason,
    entity,
    entity_id: row.id
  })
  updateRow('wxl_submission', idOf(row.submission_id), {
    current_status_id: toStatus,
    last_action_id: action.id,
    ...auditFieldsForUpdate()
  })
  updateRow(entity, String(row.id), { status_id: toStatus, ...auditFieldsForUpdate() })

  notifyAfterTransition({
    workflowCode,
    actionName,
    entity,
    entityId: String(row.id),
    reason,
    row: findRow(entity, String(row.id)) as LocalRow,
    system: true
  })
}
